import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, Plus, TrendingUp, TrendingDown, AlertTriangle, Copy, Trash2, Calendar, Thermometer, Settings, FlaskConical, Users, Upload } from 'lucide-react';
import { toast } from 'sonner';
import CalendarView from '../components/treasury/CalendarView';
import ConcentrationView from '../components/treasury/ConcentrationView';
import InvoicePlanDialog from '../components/treasury/InvoicePlanDialog';
import SimulatorView from '../components/treasury/SimulatorView';
import PartyCombobox from '../components/treasury/PartyCombobox';
import AnagraficheView from '../components/treasury/AnagraficheView';
import { ImportXmlDialog } from '../components/treasury/ImportXmlDialog';

const IVA_RATE = 0.22;

const fmt = (v) => (v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const STATO_COLOR = {
  Prevista: 'bg-slate-100 text-slate-700 border-slate-300',
  Pagata: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Incassata: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Scaduta: 'bg-red-100 text-red-800 border-red-300',
  'In ritardo': 'bg-orange-100 text-orange-800 border-orange-300',
  Annullata: 'bg-slate-200 text-slate-500 border-slate-300',
};

export default function TreasuryPage() {
  const [enums, setEnums] = useState(null);
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [parties, setParties] = useState({ client: [], supplier: [] });
  const [dashboard, setDashboard] = useState(null);
  const [invOpen, setInvOpen] = useState(false);
  const [invForm, setInvForm] = useState(emptyInv());
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyForm, setPartyForm] = useState({ party_type: 'supplier', name: '', vat: '', category: '', notes: '' });
  const [planDialog, setPlanDialog] = useState({ open: false, invoice: null });
  const [importOpen, setImportOpen] = useState(false);

  function emptyInv() {
    return {
      direction: 'out', party_id: '', party_search: '', invoice_number: '',
      invoice_date: new Date().toISOString().slice(0, 10),
      imponibile: '', category_id: '', notes: '',
      modalita_pagamento: '30 gg DF',
    };
  }

  useEffect(() => { reloadAll(); }, []);

  const reloadAll = async () => {
    try {
      const [en, cat, mov, inv, dash, pcli, psup] = await Promise.all([
        api.get('/treasury/enums'),
        api.get('/treasury/categories'),
        api.get('/treasury/movements'),
        api.get('/treasury/invoices'),
        api.get('/treasury/dashboard'),
        api.get('/treasury/parties?party_type=client'),
        api.get('/treasury/parties?party_type=supplier'),
      ]);
      setEnums(en.data);
      setCategories(cat.data);
      setMovements(mov.data);
      setInvoices(inv.data);
      setDashboard(dash.data);
      setParties({ client: pcli.data, supplier: psup.data });
    } catch (e) { toast.error('Errore caricamento'); }
  };

  const categoriesByDir = useMemo(() => {
    const wanted = invForm.direction === 'in' || invForm.direction === 'other_in' ? 'Entrata' : 'Uscita';
    return categories.filter(c => c.tipo === wanted);
  }, [categories, invForm.direction]);

  const currentParties = invForm.direction === 'in' ? parties.client : parties.supplier;

  const handleInvSubmit = async (e) => {
    e.preventDefault();
    try {
      const imp = Number(invForm.imponibile);
      if (!imp || imp <= 0) { toast.error('Imponibile obbligatorio'); return; }
      if (!invForm.category_id) { toast.error('Categoria obbligatoria'); return; }
      const iva = +(imp * IVA_RATE).toFixed(2);
      const totale = +(imp + iva).toFixed(2);
      const payload = {
        direction: invForm.direction,
        party_id: invForm.party_id || null,
        party_name_snapshot: !invForm.party_id ? (invForm.party_search || null) : null,
        invoice_number: invForm.invoice_number || null,
        invoice_date: invForm.invoice_date,
        imponibile: imp,
        iva: iva,
        totale: totale,
        category_id: invForm.category_id,
        notes: invForm.notes || null,
        modalita_pagamento: invForm.modalita_pagamento,
      };
      await api.post('/treasury/invoices', payload);
      toast.success(`Fattura registrata · Totale ${totale.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}. Puoi rientrare nella "Gestione" per anticipo/rate.`);
      setInvOpen(false);
      setInvForm(emptyInv());
      reloadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleFlag = async (mov, field) => {
    try {
      await api.patch(`/treasury/movements/${mov.id}`, { [field]: !mov[field] });
      reloadAll();
    } catch { toast.error('Errore'); }
  };

  const duplicateInvoice = async (id) => {
    try {
      await api.post(`/treasury/invoices/${id}/duplicate`, {});
      toast.success('Fattura duplicata');
      reloadAll();
    } catch { toast.error('Errore'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Eliminare la fattura e tutte le scadenze collegate?')) return;
    try {
      await api.delete(`/treasury/invoices/${id}`);
      toast.success('Eliminata');
      reloadAll();
    } catch { toast.error('Errore'); }
  };

  const handlePartySubmit = async (e) => {
    e.preventDefault();
    try {
      const { party_type, ...body } = partyForm;
      await api.post(`/treasury/parties?party_type=${party_type}`, body);
      toast.success('Anagrafica creata');
      setPartyOpen(false);
      setPartyForm({ party_type: 'supplier', name: '', vat: '', category: '', notes: '' });
      reloadAll();
    } catch { toast.error('Errore creazione anagrafica'); }
  };

  if (!enums) return <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div></div>;

  return (
    <div className="space-y-6" data-testid="treasury-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wallet className="w-6 h-6" />Tesoreria Previsionale</h1>
          <p className="text-sm text-slate-500">Pianificazione liquidità: scadenze di incasso/pagamento, no contabilità.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-xml-btn"><Upload className="w-4 h-4 mr-1" />Importa XML SDI</Button>
          <Button variant="outline" onClick={() => setPartyOpen(true)} data-testid="new-party-btn"><Plus className="w-4 h-4 mr-1" />Nuova anagrafica</Button>
          <Button onClick={() => { setInvForm(emptyInv()); setInvOpen(true); }} data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-1" />Nuova fattura</Button>
        </div>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <KpiCard label="Entrate 7gg" val={dashboard.entrate_7.amount} cnt={dashboard.entrate_7.count} color="emerald" icon={<TrendingUp className="w-4 h-4" />} />
          <KpiCard label="Entrate 30gg" val={dashboard.entrate_30.amount} cnt={dashboard.entrate_30.count} color="emerald" />
          <KpiCard label="Entrate 90gg" val={dashboard.entrate_90.amount} cnt={dashboard.entrate_90.count} color="emerald" />
          <KpiCard label="Uscite 7gg" val={dashboard.uscite_7.amount} cnt={dashboard.uscite_7.count} color="red" icon={<TrendingDown className="w-4 h-4" />} />
          <KpiCard label="Uscite 30gg" val={dashboard.uscite_30.amount} cnt={dashboard.uscite_30.count} color="red" />
          <KpiCard label="Scadute" val={null} cnt={dashboard.scadute_count} color="orange" icon={<AlertTriangle className="w-4 h-4" />} />
        </div>
      )}

      <Tabs defaultValue="calendar">
        <TabsList>
          <TabsTrigger value="calendar" data-testid="tab-calendar"><Calendar className="w-4 h-4 mr-1" />Calendario</TabsTrigger>
          <TabsTrigger value="concentration" data-testid="tab-concentration"><Thermometer className="w-4 h-4 mr-1" />Concentrazione</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="tab-simulator"><FlaskConical className="w-4 h-4 mr-1" />Simulatore</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Scadenziario ({movements.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Fatture ({invoices.length})</TabsTrigger>
          <TabsTrigger value="anagrafiche" data-testid="tab-anagrafiche"><Users className="w-4 h-4 mr-1" />Anagrafiche</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categorie ({categories.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <CalendarView movements={movements} />
        </TabsContent>

        <TabsContent value="concentration">
          <ConcentrationView movements={movements} />
        </TabsContent>

        <TabsContent value="simulator">
          <SimulatorView invoices={invoices} enums={enums} actualMovements={movements} onApplied={reloadAll} />
        </TabsContent>

        <TabsContent value="movements">
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3">Data</th><th className="py-2 px-3">Tipo</th><th className="py-2 px-3">Categoria</th>
                <th className="py-2 px-3">Anagrafica</th><th className="py-2 px-3 text-right">Importo</th>
                <th className="py-2 px-3">Stato</th><th className="py-2 px-3 text-center">Flag</th>
              </tr></thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr><td colSpan="7" className="text-center text-slate-400 py-8">Nessuna scadenza</td></tr>
                ) : movements.map(m => (
                  <tr key={m.id} className="border-b hover:bg-slate-50" data-testid={`mov-${m.id}`}>
                    <td className="py-2 px-3 font-mono text-xs">{m.date}</td>
                    <td className="py-2 px-3">{m.direction === 'in' ? <span className="text-emerald-700">↑ Entrata</span> : <span className="text-red-700">↓ Uscita</span>}</td>
                    <td className="py-2 px-3"><Badge style={{ backgroundColor: (m.category_color || '#64748b') + '22', color: m.category_color || '#64748b', borderColor: m.category_color || '#cbd5e1' }} className="border text-[10px]">{m.category_name || '—'}</Badge></td>
                    <td className="py-2 px-3 text-xs">{m.party_name || '—'} {m.is_anticipo && <Badge className="bg-blue-50 text-blue-700 border border-blue-200 ml-1 text-[9px]">Anticipo</Badge>}</td>
                    <td className="py-2 px-3 text-right font-mono">{fmt(m.amount)}</td>
                    <td className="py-2 px-3"><Badge className={`${STATO_COLOR[m.stato] || 'bg-slate-100'} border text-[10px]`}>{m.stato}</Badge></td>
                    <td className="py-2 px-3 text-center">
                      <Checkbox checked={m.direction === 'in' ? m.received_flag : m.paid_flag} onCheckedChange={() => handleFlag(m, m.direction === 'in' ? 'received_flag' : 'paid_flag')} data-testid={`flag-${m.id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3">Data</th><th className="py-2 px-3">Tipo</th><th className="py-2 px-3">N°</th>
                <th className="py-2 px-3">Anagrafica</th><th className="py-2 px-3">Categoria</th>
                <th className="py-2 px-3">Modalità</th><th className="py-2 px-3 text-right">Totale</th><th></th>
              </tr></thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan="8" className="text-center text-slate-400 py-8">Nessuna fattura</td></tr>
                ) : invoices.map(inv => (
                  <tr key={inv.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-3 font-mono text-xs">{inv.invoice_date}</td>
                    <td className="py-2 px-3">{inv.direction === 'in' ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Cliente</Badge> : inv.direction === 'out' ? <Badge className="bg-red-50 text-red-700 border border-red-200">Fornitore</Badge> : <Badge className="bg-slate-50 text-slate-700 border border-slate-200">{inv.direction}</Badge>}</td>
                    <td className="py-2 px-3 font-mono text-xs">{inv.invoice_number || '—'}</td>
                    <td className="py-2 px-3 text-xs">{inv.party_name || '—'} {inv.anticipata && <Badge className="bg-blue-50 text-blue-700 border border-blue-200 ml-1 text-[9px]">Anticipata {inv.anticipo_percentuale}%</Badge>}</td>
                    <td className="py-2 px-3"><Badge style={{ backgroundColor: (inv.category_color || '#64748b') + '22', color: inv.category_color || '#64748b', borderColor: inv.category_color || '#cbd5e1' }} className="border text-[10px]">{inv.category_name || '—'}</Badge></td>
                    <td className="py-2 px-3 text-xs">{inv.modalita_pagamento}</td>
                    <td className="py-2 px-3 text-right font-mono">{fmt(inv.totale)}</td>
                    <td className="py-2 px-3 space-x-1">
                      <Button size="sm" variant="outline" onClick={() => setPlanDialog({ open: true, invoice: inv })} title="Gestione finanziaria" data-testid={`plan-btn-${inv.id}`}><Settings className="w-3 h-3 mr-1" />Gestione</Button>
                      <Button size="sm" variant="ghost" onClick={() => duplicateInvoice(inv.id)} title="Duplica"><Copy className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteInvoice(inv.id)} title="Elimina"><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="anagrafiche">
          <AnagraficheView onPromoted={reloadAll} />
        </TabsContent>

        <TabsContent value="categories">
          <Card><CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {categories.map(c => (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded border" style={{ borderLeftColor: c.color, borderLeftWidth: 4 }}>
                  <span className="text-sm">{c.name}</span>
                  <Badge className={`text-[10px] ${c.tipo === 'Entrata' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'} border ml-auto`}>{c.tipo}</Badge>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Invoice dialog */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Fattura / Movimento</DialogTitle>
            <DialogDescription>Inserisci i dati. Il sistema genera automaticamente le scadenze di pagamento secondo la modalità scelta.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvSubmit} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Tipo *</Label>
                <Select value={invForm.direction} onValueChange={v => setInvForm({ ...invForm, direction: v, party_id: '', category_id: '' })}>
                  <SelectTrigger data-testid="inv-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Fattura cliente (entrata)</SelectItem>
                    <SelectItem value="out">Fattura fornitore (uscita)</SelectItem>
                    <SelectItem value="other_in">Altra entrata</SelectItem>
                    <SelectItem value="other_out">Altra uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{invForm.direction === 'in' ? 'Cliente' : invForm.direction === 'out' ? 'Fornitore' : 'Anagrafica (opz.)'}</Label>
                <PartyCombobox
                  parties={currentParties}
                  partyType={invForm.direction === 'in' || invForm.direction === 'other_in' ? 'client' : 'supplier'}
                  value={{ party_id: invForm.party_id, party_name_snapshot: invForm.party_search }}
                  onChange={(v) => setInvForm({ ...invForm, party_id: v.party_id || '', party_search: v.party_name_snapshot || '' })}
                  onCreateRequested={(searchTerm) => {
                    const isClient = invForm.direction === 'in' || invForm.direction === 'other_in';
                    setPartyForm({ party_type: isClient ? 'client' : 'supplier', name: searchTerm || '', vat: '', category: '', notes: '' });
                    setPartyOpen(true);
                  }}
                  testid="inv-party"
                />
              </div>
              <div>
                <Label>N° fattura</Label>
                <Input value={invForm.invoice_number} onChange={e => setInvForm({ ...invForm, invoice_number: e.target.value })} data-testid="inv-number" />
              </div>
              <div>
                <Label>Data fattura *</Label>
                <Input type="date" value={invForm.invoice_date} onChange={e => setInvForm({ ...invForm, invoice_date: e.target.value })} required data-testid="inv-date" />
              </div>
              <div>
                <Label>Categoria *</Label>
                <Select value={invForm.category_id} onValueChange={v => setInvForm({ ...invForm, category_id: v })}>
                  <SelectTrigger data-testid="inv-category"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{categoriesByDir.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Imponibile *</Label>
                <Input type="number" step="0.01" value={invForm.imponibile} onChange={e => setInvForm({ ...invForm, imponibile: e.target.value })} required data-testid="inv-imponibile" placeholder="es. 1000.00" />
              </div>
              <div>
                <Label className="text-slate-500">IVA 22% <span className="text-[10px] ml-1">(auto)</span></Label>
                <Input type="text" value={invForm.imponibile ? (Number(invForm.imponibile) * IVA_RATE).toFixed(2) : ''} readOnly disabled className="bg-slate-50 font-mono" data-testid="inv-iva-readonly" />
              </div>
              <div>
                <Label className="text-slate-500">Totale fattura <span className="text-[10px] ml-1">(auto)</span></Label>
                <Input type="text" value={invForm.imponibile ? (Number(invForm.imponibile) * (1 + IVA_RATE)).toFixed(2) : ''} readOnly disabled className="bg-slate-50 font-mono font-bold" data-testid="inv-totale-readonly" />
              </div>
              <div className="col-span-3">
                <Label>Modalità pagamento iniziale *</Label>
                <Select value={invForm.modalita_pagamento} onValueChange={v => setInvForm({ ...invForm, modalita_pagamento: v })}>
                  <SelectTrigger data-testid="inv-modalita"><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.modalita_pagamento.filter(m => !['Rate personalizzate', 'Piano di rientro personalizzato'].includes(m)).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">💡 Anticipo, rate personalizzate e modifiche scadenze si gestiscono <strong>dopo</strong> la registrazione dal pulsante <em>Gestione</em>.</p>
              </div>
            </div>

            <div><Label>Note</Label><Textarea rows={2} value={invForm.notes} onChange={e => setInvForm({ ...invForm, notes: e.target.value })} /></div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInvOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="inv-submit">Registra fattura</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Party dialog */}
      <Dialog open={partyOpen} onOpenChange={setPartyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuova anagrafica Tesoreria</DialogTitle>
            <DialogDescription>Salvata solo nell&apos;anagrafica del modulo Tesoreria.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePartySubmit} className="space-y-3">
            <div>
              <Label>Tipo *</Label>
              <Select value={partyForm.party_type} onValueChange={v => setPartyForm({ ...partyForm, party_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Cliente</SelectItem>
                  <SelectItem value="supplier">Fornitore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Ragione sociale *</Label><Input value={partyForm.name} onChange={e => setPartyForm({ ...partyForm, name: e.target.value })} required data-testid="party-name" /></div>
            <div><Label>P.IVA</Label><Input value={partyForm.vat} onChange={e => setPartyForm({ ...partyForm, vat: e.target.value })} /></div>
            <div><Label>Categoria merceologica</Label><Input value={partyForm.category} onChange={e => setPartyForm({ ...partyForm, category: e.target.value })} /></div>
            <div><Label>Note</Label><Textarea rows={2} value={partyForm.notes} onChange={e => setPartyForm({ ...partyForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPartyOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="party-submit">Crea</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice plan management dialog */}
      <InvoicePlanDialog
        open={planDialog.open}
        invoice={planDialog.invoice}
        enums={enums}
        onOpenChange={(v) => setPlanDialog(s => ({ ...s, open: v }))}
        onChanged={reloadAll}
      />

      {/* Import Fatture XML SDI (Danea Easyfatt) */}
      <ImportXmlDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={reloadAll}
      />
    </div>
  );
}

function KpiCard({ label, val, cnt, color, icon }) {
  return (
    <Card><CardContent className="p-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <span className={`text-${color}-600`}>{icon}</span>
      </div>
      {val !== null && <p className={`text-xl font-bold text-${color}-700 font-mono`}>{fmt(val)}</p>}
      <p className="text-xs text-slate-500">{cnt} {cnt === 1 ? 'movimento' : 'movimenti'}</p>
    </CardContent></Card>
  );
}
